import type {
  DashboardFilters,
  LeadershipMetrics,
  SignalDefinition,
  SignalName,
  SourceSystem,
} from '@delivery-metrics/shared';
import {
  BugReport,
  FindInPage,
  InfoOutlined,
  Loop,
  Speed,
  TrendingUp,
  Warning,
} from '@mui/icons-material';
import AccountTree from '@mui/icons-material/AccountTree';
import MenuBook from '@mui/icons-material/MenuBook';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Tooltip as MuiTooltip,
  Typography,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { getDevToolsConfig } from '../../config/env';
import {
  PythonApiService,
  type LeadershipKPIOverview,
  type MetricDataPoint,
  type PythonKPIs,
} from '../../services/python-api';
import {
  appendKPISummaryUnit,
  type KPISummaryUnit,
} from '../../utils/kpiSummary';
import { KPIProgressBar } from '../shared/KPIProgressBar';

/**
 * URL for the Signal Glossary page published via Backstage TechDocs.
 * Configurable via VITE_SIGNAL_GLOSSARY_URL environment variable.
 */
const SIGNAL_GLOSSARY_URL =
  import.meta.env.VITE_SIGNAL_GLOSSARY_URL ??
  'https://portal.internal.philips/docs/default/component/ei-rd-eff-deliverymetrics/metrics/signal-glossary';

/**
 * URL for the Signal Mapping (team-datasource-mapping) page published via Backstage TechDocs.
 * Configurable via VITE_SIGNAL_MAPPING_URL environment variable.
 */
const SIGNAL_MAPPING_URL =
  import.meta.env.VITE_SIGNAL_MAPPING_URL ??
  'https://portal.internal.philips/docs/default/component/ei-rd-eff-deliverymetrics/metrics/team-datasource-mapping';

/**
 * Signal definitions consistent with docs/metrics/signal-glossary.md.
 * Drives tooltip content for underlying metrics beneath each KPI card.
 */
const SIGNAL_DEFINITIONS: Record<SignalName, SignalDefinition> = {
  cycle_time: {
    name: 'cycle_time',
    displayName: 'Cycle Time',
    description:
      "The number of days from a developer's first commit to the pull request being merged into main. Measures how quickly a code change travels through development and review.",
    unit: 'days',
    sourceSystem: 'github' as SourceSystem,
    kpiDirection: 'lower_is_better',
    targetPct: 10,
    formula: '((last_value / first_value) - 1) × -1 × 100',
    valueRationale:
      'Shorter cycle times mean faster feedback loops and quicker value delivery to customers.',
  },
  delivery_velocity: {
    name: 'delivery_velocity',
    displayName: 'Delivery Velocity',
    description:
      'The combined count of merged pull requests and closed features per period. Measures how much work the team ships over time.',
    unit: 'items/period',
    sourceSystem: 'github' as SourceSystem,
    sourceLabel: 'GitHub + Azure DevOps or Jira',
    kpiDirection: 'higher_is_better',
    targetPct: 10,
    formula: '((last_value / first_value) - 1) × 100',
    valueRationale:
      'Higher velocity indicates the team is delivering more value per unit of time.',
  },
  test_coverage: {
    name: 'test_coverage',
    displayName: 'Test Coverage',
    description:
      'The percentage of source code lines exercised by automated tests. Higher coverage means more of the codebase is verified automatically, reducing the chance of undetected bugs.',
    unit: '%',
    sourceSystem: 'sonarqube' as SourceSystem,
    sourceLabel: 'SonarQube + TICS',
    kpiDirection: 'higher_is_better',
    targetPct: 10,
    formula: '((last_value / first_value) - 1) × 100',
    valueRationale:
      'Greater test coverage reduces the risk of regressions and increases confidence in releases.',
  },
  defect_escape_rate: {
    name: 'defect_escape_rate',
    displayName: 'Defect Escape Rate',
    description:
      'The share of defects that slip past all internal testing phases and are discovered by customers in the field. DER = field defects / total defects (engineering + V&V + field).',
    unit: '%',
    sourceSystem: 'pdp_metrics_proxy' as SourceSystem,
    kpiDirection: 'lower_is_better',
    targetPct: 10,
    formula: '((last_value / first_value) - 1) × -1 × 100',
    valueRationale:
      'A lower defect escape rate means quality gates are catching more issues before they reach customers.',
  },
  on_time_predictability: {
    name: 'on_time_predictability',
    displayName: 'On Time / Predictability',
    description:
      'Share of planned features actually delivered on time, measured at both the Program Increment (PI) and Release level. Answers whether teams deliver what they planned, when they planned it.',
    unit: '%',
    sourceSystem: 'azure_devops' as SourceSystem,
    sourceLabel: 'Azure DevOps',
    kpiDirection: 'higher_is_better',
    targetPct: 10,
    formula:
      'PI Predictability % = (features accepted / features planned) × 100; Release Predictability % = (features released / features planned) × 100',
    valueRationale:
      'Gives R&D leadership a near-real-time, objective measure of planning accuracy at PI and Release level, enabling earlier corrective action and more reliable commitments to stakeholders.',
  },
};

/** Human-readable labels for source systems. */
const SOURCE_SYSTEM_LABELS: Record<SourceSystem, string> = {
  github: 'GitHub',
  azure_devops: 'Azure DevOps',
  sonarqube: 'SonarQube',
  ado: 'Azure DevOps',
  jira: 'JIRA',
  tics: 'TICS',
};

/**
 * Data quality status for a signal. Populated from the data quality API
 * when available; for now uses a placeholder that can be wired up later.
 */
interface SignalDataQualityStatus {
  hasUnresolvedAnomalies: boolean;
  anomalySummary: string;
}

interface EIRnDTopLevelKPISectionProps {
  data: LeadershipMetrics | null;
  loading?: boolean;
  filters: DashboardFilters;
}

interface KpiCardConfig {
  title: string;
  icon: React.ReactNode;
  value: number;
  summaryUnit: KPISummaryUnit;
  target: string;
  trend: 'up' | 'down';
  color: string;
  description: string;
  baseValue: number;
  trendTitle: string;
  tooltip: string;
}

/**
 * Resolve the signal/metric key for a KPI card from its title.
 * Extracted to a named helper to keep the card renderer's cognitive
 * complexity low and avoid nested ternaries.
 */
const resolveMetricKey = (title: string): SignalName => {
  if (title.includes('Cycle Time')) {
    return 'cycle_time';
  }
  if (title.includes('Delivery Velocity')) {
    return 'delivery_velocity';
  }
  if (title.includes('Test Coverage')) {
    return 'test_coverage';
  }
  return 'defect_escape_rate';
};

/**
 * Resolve the data-source label shown beneath a KPI card from its signal
 * definition (KPI 3 aggregates SonarQube + TICS via its ``sourceLabel``).
 */
const resolveSourceLabel = (signalDef: SignalDefinition): string =>
  signalDef.sourceLabel ?? SOURCE_SYSTEM_LABELS[signalDef.sourceSystem];

const formatKPISummaryValue = (value: number, unit: KPISummaryUnit): string => {
  if (!Number.isFinite(value)) {
    return appendKPISummaryUnit(0, unit);
  }

  const rounded = Math.round(value);
  if (rounded !== 0 || value === 0) {
    return appendKPISummaryUnit(rounded, unit);
  }

  return appendKPISummaryUnit(value.toFixed(1), unit);
};

// Mock trend data - in production this would come from the API
// Data varies based on filters to simulate real behavior
const generateTrendData = (
  baseValue: number,
  trend: 'up' | 'down',
  label: string,
  filters: DashboardFilters
) => {
  const data = [];

  // Calculate the number of data points based on the time range
  const startDate = new Date(filters.timeRange.start);
  const endDate = new Date(filters.timeRange.end);
  const daysDiff = Math.ceil(
    (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)
  );

  // Determine appropriate granularity and number of points
  let numPoints: number;
  let labelFormat: (date: Date) => string;
  let intervalDays: number;

  if (daysDiff <= 14) {
    // Daily for 2 weeks or less
    numPoints = Math.max(7, daysDiff);
    intervalDays = Math.max(1, Math.floor(daysDiff / numPoints));
    labelFormat = date =>
      date.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        timeZone: 'UTC',
      });
  } else if (daysDiff <= 90) {
    // Weekly intervals but show dates for up to 3 months
    numPoints = Math.ceil(daysDiff / 7);
    intervalDays = 7;
    labelFormat = date =>
      date.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        timeZone: 'UTC',
      });
  } else if (daysDiff <= 365) {
    // Monthly for up to a year
    numPoints = Math.ceil(daysDiff / 30);
    intervalDays = Math.floor(daysDiff / numPoints);
    labelFormat = date =>
      date.toLocaleDateString(undefined, { month: 'short', timeZone: 'UTC' });
  } else {
    // Quarterly for more than a year
    numPoints = Math.ceil(daysDiff / 90);
    intervalDays = Math.floor(daysDiff / numPoints);
    labelFormat = date =>
      `Q${Math.ceil((date.getUTCMonth() + 1) / 3)} ${date.getUTCFullYear()}`;
  }

  // Create a seed based on filters to make data consistent for same filter selections
  const filterSeed = JSON.stringify({
    teams: filters.teams?.sort(),
    programs: filters.programs?.sort(),
    repositories: filters.repositories?.sort(),
    timeRange: filters.timeRange.preset,
  });

  // Simple hash function for consistent randomization
  const hashCode = (str: string) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash;
    }
    return Math.abs(hash);
  };

  const seed = hashCode(filterSeed);

  // Seeded random function
  const seededRandom = (index: number) => {
    const x = Math.sin(seed + index) * 10000;
    return x - Math.floor(x);
  };

  // Adjust base value based on filter selections
  let adjustedBase = baseValue;
  if (filters.teams && filters.teams.length > 0) {
    adjustedBase *= 1 + filters.teams.length * 0.05;
  }
  if (filters.programs && filters.programs.length > 0) {
    adjustedBase *= 1 + filters.programs.length * 0.03;
  }

  for (let i = 0; i < numPoints; i++) {
    const currentDate = new Date(startDate);
    currentDate.setDate(startDate.getDate() + i * intervalDays);

    // Create unique patterns for each specific metric
    const progress = i / numPoints;
    let sineWave, noiseLevel, trendSteepness, phaseShift;

    // Assign distinct characteristics based on metric title
    if (label.includes('Cycle Time')) {
      // Smooth, gradual improvement with few peaks
      sineWave = Math.sin(progress * Math.PI * 2) * 0.2;
      noiseLevel = 0.15;
      trendSteepness = 0.4;
      phaseShift = 0;
    } else if (label.includes('Delivery Velocity')) {
      // More volatile with frequent peaks and valleys
      sineWave = Math.sin(progress * Math.PI * 5 + 1) * 0.35;
      noiseLevel = 0.5;
      trendSteepness = 0.5;
      phaseShift = 1;
    } else if (label.includes('Test Coverage')) {
      // Steady climb with moderate variation
      sineWave = Math.sin(progress * Math.PI * 3 + 2) * 0.25;
      noiseLevel = 0.25;
      trendSteepness = 0.6;
      phaseShift = 2;
    } else {
      // Defect Escape Rate - irregular pattern with sharp changes
      sineWave = Math.sin(progress * Math.PI * 4 + 3) * 0.4;
      noiseLevel = 0.4;
      trendSteepness = 0.35;
      phaseShift = 3;
    }

    const randomNoise = (seededRandom(i + phaseShift * 100) - 0.5) * noiseLevel;
    const trendComponent =
      trend === 'up' ? progress * trendSteepness : -progress * trendSteepness;

    const variance = (sineWave + randomNoise + trendComponent) * adjustedBase;

    data.push({
      week: labelFormat(currentDate),
      value: Math.max(0, adjustedBase + variance),
      label,
      trendline: 0, // Will be calculated after all points are generated
    });
  }

  // Calculate linear regression trendline
  const n = data.length;
  let sumX = 0,
    sumY = 0,
    sumXY = 0,
    sumX2 = 0;

  data.forEach((point, index) => {
    sumX += index;
    sumY += point.value;
    sumXY += index * point.value;
    sumX2 += index * index;
  });

  const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
  const intercept = (sumY - slope * sumX) / n;

  // Add trendline values to each data point
  data.forEach((point, index) => {
    point.trendline = slope * index + intercept;
  });

  return data;
};

export const EIRnDTopLevelKPISection: React.FC<
  EIRnDTopLevelKPISectionProps
> = ({ data, loading, filters }) => {
  const [pythonKPIs, setPythonKPIs] = useState<PythonKPIs | null>(null);
  const [leadershipKPIs, setLeadershipKPIs] =
    useState<LeadershipKPIOverview | null>(null);
  const [metricsData, setMetricsData] = useState<
    Record<string, MetricDataPoint[]>
  >({});
  const [isLoadingPythonData, setIsLoadingPythonData] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const useMockData = getDevToolsConfig().mockData || false;

  // Data quality status per signal — placeholder state to be populated from
  // /api/metrics/data-quality/status when the endpoint is available.
  const [dataQualityStatus, setDataQualityStatus] = useState<
    Record<SignalName, SignalDataQualityStatus>
  >({
    cycle_time: { hasUnresolvedAnomalies: false, anomalySummary: '' },
    delivery_velocity: { hasUnresolvedAnomalies: false, anomalySummary: '' },
    test_coverage: { hasUnresolvedAnomalies: false, anomalySummary: '' },
    defect_escape_rate: { hasUnresolvedAnomalies: false, anomalySummary: '' },
    on_time_predictability: {
      hasUnresolvedAnomalies: false,
      anomalySummary: '',
    },
  });

  // TODO: Fetch data quality status from API when endpoint exists.
  // useEffect(() => {
  //   PythonApiService.getDataQualityStatus().then(setDataQualityStatus);
  // }, [filters]);

  // Expose setter so external code can populate quality status later.
  // This avoids an unused-variable lint warning while keeping the state wired up.
  void setDataQualityStatus;

  // Fetch real data from Python API
  useEffect(() => {
    if (useMockData) return;

    let cancelled = false;
    const fetchPythonData = async () => {
      setIsLoadingPythonData(true);
      setApiError(null);
      try {
        console.debug('Fetching Python API data...');
        const fetchAll = Promise.all([
          PythonApiService.getKPIs(filters),
          PythonApiService.getLeadershipKPIs(filters),
          PythonApiService.getCycleTimeMetric(filters),
          PythonApiService.getDeliveryVelocityMetric(filters),
          PythonApiService.getTestCoverageMetric(filters),
          PythonApiService.getDefectEscapeRateMetric(filters),
        ]);

        const [kpis, leadership, cycleTime, velocity, efficiency, defectRate] =
          await fetchAll;

        // A newer source/filter selection superseded this request while it
        // was in flight; drop the stale response so it cannot overwrite the
        // fresh data (e.g. rapid SonarQube/TICS toggling).
        if (cancelled) return;

        console.debug('Component received leadership KPIs:', leadership);
        console.debug(
          'Full response: kpis=%O, leadership=%O, cycleTime=%O, velocity=%O',
          kpis,
          leadership,
          cycleTime,
          velocity
        );

        setPythonKPIs(kpis);
        console.debug('State updated - setPythonKPIs');
        setLeadershipKPIs(leadership);
        console.debug('State updated - setLeadershipKPIs with:', leadership);
        setMetricsData({
          cycle_time: cycleTime,
          delivery_velocity: velocity,
          test_coverage: efficiency,
          defect_escape_rate: defectRate,
        });

        const allSeriesEmpty = [
          cycleTime,
          velocity,
          efficiency,
          defectRate,
        ].every(series => !series.length);
        if (allSeriesEmpty) {
          setApiError('Metrics API returned no data for the selected filters.');
        }
      } catch (error) {
        if (cancelled) return;
        console.error('Error fetching Python API data:', error);
        console.error(
          'Error details - full error object:',
          JSON.stringify(error)
        );
        setPythonKPIs(null);
        setLeadershipKPIs(null);
        setMetricsData({});
        setApiError(
          'Unable to load KPI metrics from API. Please retry in a moment.'
        );
      } finally {
        if (!cancelled) {
          setIsLoadingPythonData(false);
        }
      }
    };

    fetchPythonData();
    return () => {
      cancelled = true;
    };
  }, [filters, useMockData]);

  const kpis = useMemo(() => {
    // Use mock data only when explicitly enabled; otherwise stay API-driven.
    console.debug(
      'KPI useMemo calculating - leadershipKPIs state:',
      leadershipKPIs
    );
    let cycleTimeValue, velocityValue, efficiencyValue, defectValue;

    if (!useMockData) {
      // Use leadership improvement percentages for top cards.
      const ct = Number(leadershipKPIs?.cycle_time_reduction_pct ?? 0);
      const vel = Number(leadershipKPIs?.delivery_velocity_increase_pct ?? 0);
      const eff = Number(leadershipKPIs?.test_coverage_increase_pct ?? 0);
      const def_ = Number(
        leadershipKPIs?.defect_escape_rate_reduction_pct ?? 0
      );

      console.debug(
        'Extracted KPI values - ct:',
        ct,
        'vel:',
        vel,
        'eff:',
        eff,
        'def_:',
        def_
      );

      cycleTimeValue = ct;
      velocityValue = vel;
      efficiencyValue = eff;
      defectValue = def_;
    }

    if (useMockData && cycleTimeValue === undefined) {
      // Calculate dynamic mock values based on filters
      const filterSeed = JSON.stringify({
        teams: filters.teams?.sort(),
        programs: filters.programs?.sort(),
        repositories: filters.repositories?.sort(),
        timeRange: filters.timeRange.preset,
      });

      const hashCode = (str: string) => {
        let hash = 0;
        for (let i = 0; i < str.length; i++) {
          const char = str.charCodeAt(i);
          hash = (hash << 5) - hash + char;
          hash = hash & hash;
        }
        return Math.abs(hash);
      };

      const seed = hashCode(filterSeed);
      const seededRandom = (offset: number) => {
        const x = Math.sin(seed + offset) * 10000;
        return x - Math.floor(x);
      };

      // Base values that vary with filters
      const teamMultiplier = 1 + (filters.teams?.length || 0) * 0.02;
      const programMultiplier = 1 + (filters.programs?.length || 0) * 0.015;
      const repoMultiplier = 1 + (filters.repositories?.length || 0) * 0.01;

      cycleTimeValue = -Math.round((3 + seededRandom(1) * 4) * teamMultiplier);
      velocityValue = -Math.round(
        (2 + seededRandom(2) * 5) * programMultiplier
      );
      efficiencyValue = Math.round((4 + seededRandom(3) * 4) * repoMultiplier);
      defectValue = Math.round((5 + seededRandom(4) * 4) * teamMultiplier);
    }

    if (cycleTimeValue === undefined) cycleTimeValue = 0;
    if (velocityValue === undefined) velocityValue = 0;
    if (efficiencyValue === undefined) efficiencyValue = 0;
    if (defectValue === undefined) defectValue = 0;

    return [
      {
        title: 'KPI 1: Cycle Time Reduction',
        icon: <Loop color='primary' />,
        value: cycleTimeValue,
        summaryUnit: leadershipKPIs?.summaryUnits?.cycle_time ?? 'percentage',
        target: '10%',
        trend: (cycleTimeValue < 0 ? 'up' : 'down') as 'up' | 'down',
        color: '#1976d2',
        description: 'Cycle time in days',
        baseValue: 15,
        trendTitle: 'Cycle Time',
        tooltip:
          "Cycle Time Reduction measures how much faster code changes move through the pipeline over the selected date range. Cycle time is the number of days from a developer's first commit to the pull request being merged.\n\nThe KPI value is derived from the linear trend line (the dotted line in the chart), not from the raw bucket values: it compares the trend line at the first and last complete bucket, so a single noisy period does not dominate the result.\n\nData source: GitHub pull requests\nTarget: 10% reduction in 2026",
      },
      {
        title: 'KPI 2: Delivery Velocity Increase',
        icon: <Speed color='success' />,
        value: velocityValue,
        summaryUnit:
          leadershipKPIs?.summaryUnits?.delivery_velocity ?? 'percentage',
        target: '10%',
        trend: (velocityValue < 0 ? 'down' : 'up') as 'up' | 'down',
        color: '#2e7d32',
        description: 'Features + pull requests per period',
        baseValue: 12,
        trendTitle: 'Delivery Velocity',
        tooltip:
          'Delivery Velocity Increase measures how much more work the team ships over the selected date range. Velocity is the combined count of merged pull requests and closed features per period.\n\nThe KPI value is derived from the linear trend line (the dotted line in the chart), not from the raw bucket values: it compares the trend line at the first and last complete bucket, so a single noisy period does not dominate the result.\n\nData sources: GitHub (merged PRs) + Azure DevOps or Jira (closed features)\nTarget: 10% increase in 2026',
      },
      {
        title: 'KPI 3: Test Coverage Increase',
        icon: <FindInPage sx={{ color: '#7b1fa2' }} />,
        value: efficiencyValue,
        summaryUnit:
          leadershipKPIs?.summaryUnits?.test_coverage ?? 'percentage_points',
        target: '10 %pt',
        trend: 'up' as const,
        color: '#7b1fa2',
        description: 'Coverage %',
        baseValue: 8,
        trendTitle: 'Test Coverage',
        tooltip:
          'Test Coverage Increase measures the percentage-point increase in code coverage over the selected date range. Higher coverage means more of the codebase is verified automatically.\n\nThe KPI value is derived from the linear trend line (the dotted line in the chart), not from the raw bucket values: it compares the trend line at the first and last complete bucket, so a single noisy period does not dominate the result.\n\nPercentage points (%pt) measure the arithmetic difference between percentages; for example, 20% minus 15% equals 5 %pt.\n\nData sources: SonarQube + TICS\nTarget: 10 %pt increase in 2026',
      },
      {
        title: 'KPI 4: Defect Escape Rate Reduction',
        icon: <BugReport sx={{ color: '#0097a7' }} />,
        value: defectValue,
        summaryUnit:
          leadershipKPIs?.summaryUnits?.defect_escape_rate ??
          'percentage_points',
        target: '10 %pt',
        trend: 'down' as const,
        color: '#0097a7',
        description: 'Post-RfD defects compared to total defects',
        baseValue: 25,
        trendTitle: 'Defect Escape Rate',
        tooltip:
          'Defect Escape Rate Reduction measures the percentage-point reduction in the share of defects found by customers in the field over the selected date range. DER = field defects / total defects (engineering + V&V + field).\n\nThe KPI value is derived from the linear trend line (the dotted line in the chart), not from the raw bucket values: it compares the trend line at the first and last complete bucket, so a single noisy period does not dominate the result.\n\nPercentage points (%pt) measure the arithmetic difference between percentages; for example, 20% minus 15% equals 5 %pt.\n\nData source: Azure DevOps and Jira defects (metrics.defects)\nTarget: 10 %pt reduction in 2026',
      },
    ] satisfies KpiCardConfig[];
  }, [filters, leadershipKPIs, pythonKPIs, useMockData]);

  if (loading || !data || (isLoadingPythonData && !useMockData)) {
    return (
      <Box sx={{ mb: 4 }}>
        <Typography
          variant='h5'
          gutterBottom
          sx={{ display: 'flex', alignItems: 'center', mb: 3 }}
        >
          <TrendingUp sx={{ mr: 1 }} />
          R&D Effectiveness KPIs
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              md: 'repeat(2, 1fr)',
              lg: 'repeat(4, 1fr)',
            },
            gap: 3,
          }}
        >
          {[1, 2, 3, 4].map(i => (
            <Card key={i}>
              <CardContent>
                <Typography variant='body2' color='text.secondary'>
                  Loading...
                </Typography>
              </CardContent>
            </Card>
          ))}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 4 }}>
      {apiError && !useMockData && (
        <Alert severity='warning' sx={{ mb: 2 }}>
          {apiError}
        </Alert>
      )}

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 3,
        }}
      >
        <Typography
          variant='h5'
          gutterBottom
          sx={{ display: 'flex', alignItems: 'center', mb: 0 }}
        >
          <TrendingUp sx={{ mr: 1 }} />
          EI R&D Top Level KPIs
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            component='a'
            href={SIGNAL_GLOSSARY_URL}
            target='_blank'
            rel='noopener noreferrer'
            size='small'
            startIcon={<MenuBook />}
            sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
          >
            Signal Glossary
          </Button>
          <Button
            component='a'
            href={SIGNAL_MAPPING_URL}
            target='_blank'
            rel='noopener noreferrer'
            size='small'
            startIcon={<AccountTree />}
            sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
          >
            Signal Mapping
          </Button>
        </Box>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            md: 'repeat(2, 1fr)',
          },
          gap: 3,
        }}
      >
        {kpis.map((kpi, index: number) => {
          const metricKey = resolveMetricKey(kpi.title);

          const signalDef = SIGNAL_DEFINITIONS[metricKey];
          const qualityStatus = dataQualityStatus[metricKey];
          const effectiveSourceLabel = resolveSourceLabel(signalDef);

          const formattedValue = formatKPISummaryValue(
            kpi.value,
            kpi.summaryUnit
          );
          const progressValue = Number.isFinite(kpi.value) ? kpi.value : 0;

          const rawApiSeries = !useMockData
            ? metricsData[metricKey]
            : undefined;
          // Only use API data if it has meaningful (non-zero) values
          const apiSeries =
            rawApiSeries &&
            rawApiSeries.length > 0 &&
            rawApiSeries.some(p => p.value !== 0)
              ? rawApiSeries
              : undefined;
          const apiTrendLine = leadershipKPIs?.trendLines?.[metricKey];
          const hasApiTrendLine =
            apiSeries !== undefined &&
            apiTrendLine !== undefined &&
            apiTrendLine.length === apiSeries.length;

          const trendData =
            apiSeries && apiSeries.length > 0
              ? apiSeries.map((point, pointIndex) => ({
                  week:
                    point.label ||
                    new Date(point.timestamp).toLocaleDateString(undefined, {
                      month: 'short',
                      day: 'numeric',
                    }),
                  value: point.value,
                  label: kpi.title,
                  trendline: hasApiTrendLine
                    ? (apiTrendLine?.[pointIndex] ?? 0)
                    : 0,
                }))
              : useMockData
                ? generateTrendData(
                    kpi.baseValue,
                    kpi.trend,
                    kpi.title,
                    filters
                  )
                : [];

          // Add trendline for API-backed series to keep chart rendering consistent.
          if (trendData.length > 1 && !hasApiTrendLine) {
            const n = trendData.length;
            let sumX = 0,
              sumY = 0,
              sumXY = 0,
              sumX2 = 0;

            trendData.forEach((point, pointIndex) => {
              sumX += pointIndex;
              sumY += point.value;
              sumXY += pointIndex * point.value;
              sumX2 += pointIndex * pointIndex;
            });

            const denominator = n * sumX2 - sumX * sumX;
            const slope =
              denominator !== 0 ? (n * sumXY - sumX * sumY) / denominator : 0;
            const intercept = (sumY - slope * sumX) / n;

            trendData.forEach((point, pointIndex) => {
              point.trendline = slope * pointIndex + intercept;
            });
          }

          return (
            <Card key={index}>
              <CardContent sx={{ '&:last-child': { pb: 0 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  {kpi.icon}
                  <Typography
                    variant='h6'
                    sx={{ ml: 1, fontSize: '0.95rem', flex: 1 }}
                  >
                    {kpi.title}
                  </Typography>
                  {qualityStatus.hasUnresolvedAnomalies && (
                    <MuiTooltip
                      title={
                        <Typography
                          variant='body2'
                          sx={{ whiteSpace: 'pre-line', p: 0.5 }}
                        >
                          {`⚠ Data Quality Warning\n${qualityStatus.anomalySummary}\n\nThe displayed value may be stale.`}
                        </Typography>
                      }
                      arrow
                      placement='top'
                    >
                      <Warning
                        sx={{
                          fontSize: 18,
                          color: 'warning.main',
                          cursor: 'help',
                          ml: 0.5,
                        }}
                        data-testid={`quality-warning-${metricKey}`}
                      />
                    </MuiTooltip>
                  )}
                  <MuiTooltip
                    title={
                      <Typography
                        variant='body2'
                        sx={{ whiteSpace: 'pre-line', p: 0.5 }}
                      >
                        {kpi.tooltip}
                      </Typography>
                    }
                    arrow
                    placement='top'
                  >
                    <InfoOutlined
                      sx={{
                        fontSize: 18,
                        color: 'text.secondary',
                        cursor: 'help',
                        ml: 0.5,
                      }}
                    />
                  </MuiTooltip>
                </Box>

                <Box sx={{ mb: 1 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      mb: 0.5,
                    }}
                  >
                    <Typography variant='h5' sx={{ color: kpi.color }}>
                      {formattedValue}
                    </Typography>
                    <Typography variant='caption' color='text.secondary'>
                      Target: {kpi.target}
                    </Typography>
                  </Box>
                  <KPIProgressBar
                    value={progressValue}
                    valueUnit={kpi.summaryUnit}
                    target={10}
                    themeColor={kpi.color}
                  />
                </Box>

                <Divider
                  sx={{ mt: 5, mb: 2, mx: '40%', borderColor: 'transparent' }}
                />

                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography
                      variant='subtitle2'
                      color='text.secondary'
                      sx={{ mt: 0, mb: 0 }}
                    >
                      {kpi.trendTitle} Trend
                    </Typography>
                    <MuiTooltip
                      title={
                        <Box sx={{ p: 0.5 }}>
                          <Typography
                            variant='subtitle2'
                            sx={{ fontWeight: 'bold', mb: 0.5 }}
                          >
                            {signalDef.displayName}
                          </Typography>
                          <Typography variant='body2' sx={{ mb: 0.5 }}>
                            {signalDef.description}
                          </Typography>
                          <Typography
                            variant='caption'
                            sx={{ display: 'block' }}
                          >
                            Source: {effectiveSourceLabel}
                          </Typography>
                        </Box>
                      }
                      arrow
                      placement='top'
                    >
                      <InfoOutlined
                        sx={{
                          fontSize: 14,
                          color: 'text.secondary',
                          cursor: 'help',
                        }}
                      />
                    </MuiTooltip>
                  </Box>

                  <Typography
                    variant='caption'
                    color='text.secondary'
                    sx={{ display: 'block', mb: 1 }}
                  >
                    {kpi.description}
                  </Typography>
                </Box>

                <ResponsiveContainer width='100%' height={150}>
                  <ComposedChart data={trendData}>
                    <defs>
                      <linearGradient
                        id={`gradient-${index}`}
                        x1='0'
                        y1='0'
                        x2='0'
                        y2='1'
                      >
                        <stop
                          offset='5%'
                          stopColor={kpi.color}
                          stopOpacity={0.3}
                        />
                        <stop
                          offset='95%'
                          stopColor={kpi.color}
                          stopOpacity={0}
                        />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray='3 3' stroke='#f0f0f0' />
                    <XAxis
                      dataKey='week'
                      tick={{ fontSize: 10 }}
                      interval='preserveStartEnd'
                      angle={-45}
                      textAnchor='end'
                      height={60}
                    />
                    <YAxis tick={{ fontSize: 10 }} width={30} />
                    <Tooltip
                      contentStyle={{
                        fontSize: 11,
                        padding: '4px 8px',
                        border: '1px solid #ccc',
                        borderRadius: '4px',
                      }}
                      content={({ active, payload }) => {
                        if (!active || !payload || !payload.length) return null;
                        const data = payload[0].payload;
                        return (
                          <div
                            style={{
                              fontSize: 11,
                              padding: '6px 10px',
                              backgroundColor: 'white',
                              border: '1px solid #ccc',
                              borderRadius: '4px',
                              maxWidth: 260,
                            }}
                          >
                            <div style={{ fontWeight: 600, marginBottom: 2 }}>
                              {signalDef.displayName}
                            </div>
                            <div>
                              {data.week}:{' '}
                              {signalDef.unit === 'items/period'
                                ? Math.round(data.value)
                                : data.value.toFixed(1)}
                              {signalDef.unit === '%'
                                ? '%'
                                : ` ${signalDef.unit}`}
                            </div>
                            <div
                              style={{
                                color: '#666',
                                marginTop: 2,
                                fontSize: 10,
                              }}
                            >
                              Source: {effectiveSourceLabel}
                            </div>
                          </div>
                        );
                      }}
                    />
                    <Area
                      type='monotone'
                      dataKey='value'
                      stroke={kpi.color}
                      strokeWidth={2}
                      fill={`url(#gradient-${index})`}
                    />
                    <Line
                      type='linear'
                      dataKey='trendline'
                      stroke={kpi.color}
                      strokeWidth={1}
                      strokeDasharray='5 5'
                      dot={false}
                      activeDot={false}
                      isAnimationActive={false}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          );
        })}
      </Box>
    </Box>
  );
};
